'use client'

import { usePathname } from 'next/navigation'
import { useLoginState } from '@/hooks/use-login-state'
import { cn } from '@/lib/utils'
import type { User } from '@/payload-types'

type LoginRole = NonNullable<User['roles']>[number]
type IntranetRole = NonNullable<User['intranet_roles']>[number]

function canSeeEventManagementLinks(roles: LoginRole[], intranetRoles: IntranetRole[]): boolean {
  if (roles.includes('admin')) return true
  return intranetRoles.includes('team_leader') || intranetRoles.includes('regional_coordinator')
}

function canEditEventInPayload(roles: LoginRole[]): boolean {
  return roles.includes('admin') || roles.includes('intranet')
}

type EventManagementSubnavProps = {
  eventId: string
  basePath?: string
}

const linkBaseClass =
  'flex h-full items-center whitespace-nowrap border-r border-theme-surface-border px-4 text-sm font-semibold no-underline transition-colors hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground'

function getNavItems(eventId: string, basePath: string) {
  const base = `${basePath}/${eventId}`
  return [
    { label: 'Event Details', href: base },
    { label: 'Volunteers', href: `${base}/volunteers` },
    { label: 'Event Report', href: `${base}/report` },
    { label: 'Reviews', href: `${base}/reviews` },
  ] as const
}

export function EventManagementSubnav({
  eventId,
  basePath = '/events',
}: EventManagementSubnavProps) {
  const { isLoading, isLoggedIn, roles, intranetRoles } = useLoginState()
  const pathname = usePathname() ?? ''
  const showManagementLinks = canSeeEventManagementLinks(roles, intranetRoles)
  const showEditEventLink = canEditEventInPayload(roles)

  if (isLoading || !isLoggedIn || (!showManagementLinks && !showEditEventLink)) {
    return null
  }

  const items = getNavItems(eventId, basePath)

  return (
    <div
      className="overflow-auto sticky top-13.75 z-20 flex h-11 border-b border-theme-surface-border md:top-17.5 bg-theme-row-2 font-mono uppercase text-sm"
    >
      <div className="flex h-full min-w-max">
        {showManagementLinks && items.map(({ label, href }, index) => {
          const active = pathname === href
          const className = cn(
            linkBaseClass,
            'text-theme-button-secondary-foreground',
            active && 'bg-theme-button-primary-background text-theme-button-primary-foreground',
          )
          const key = `${label}-${href}-${index}`
          return (
            <a key={key} href={href} className={className}>
              {label}
            </a>
          )
        })}
        {showEditEventLink && (
          <a
            href={`/admin/collections/events/${eventId}`}
            className={cn(
              linkBaseClass,
              'bg-transparent text-theme-button-secondary-foreground hover:bg-theme-row-1 hover:text-theme-button-secondary-foreground',
            )}
          >
            Edit Event
          </a>
        )}
      </div>
    </div>
  )
}
